// The lab's side of the 3D setup: import a plan out of the CAD, look at it, send it to the doctor,
// and work through whatever they asked for.
//
// The doctor's requests land here as annotations, and the technician ticks them off as they apply
// each one in the design software. Applying them produces a NEW revision — a shared revision is
// never edited, because an approval names the exact revision and hash the doctor looked at.
import { useCallback, useEffect, useState } from 'react';
import {
  SETUP_SOURCE_LABELS,
  SETUP_STATUS_LABELS,
  type CaseSetup,
  type SetupView,
  type WfCaseWorkspace,
  type WfPriority,
} from '@alignova/shared';
import { useCan } from '../../auth/useCan.ts';
import { SetupWorkspace } from '../../ui/SetupWorkspace.tsx';
import { importSetupPlan, isArchiveOutcome, uploadCaseFile, wfApi } from './workflowApi.ts';
import { Btn, Empty, ErrorText, fmtDT, inputCls } from './wfUi.tsx';
import { TrayCountRequired } from './WfCompliance.tsx';

export function SetupPanel({ ws, onChanged }: { ws: WfCaseWorkspace; onChanged: () => void }) {
  const can = useCan();
  const caseId = ws.case.id;
  const [list, setList] = useState<CaseSetup[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [view, setView] = useState<SetupView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadList = useCallback(() => {
    wfApi
      .setups(caseId)
      .then((rows) => {
        setList(rows);
        setOpenId((cur) => cur ?? rows[0]?.id ?? null);
      })
      .catch((e: Error) => setErr(e.message));
  }, [caseId]);
  useEffect(loadList, [loadList]);

  const loadView = useCallback(() => {
    if (!openId) return setView(null);
    wfApi.setup(openId).then(setView).catch((e: Error) => setErr(e.message));
  }, [openId]);
  useEffect(loadView, [loadView]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      loadList();
      loadView();
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const canEdit = can('workflow:update');

  return (
    <div className="space-y-4">
      {canEdit && <ImportForm ws={ws} onDone={(id) => { setOpenId(id); loadList(); onChanged(); }} />}
      <ErrorText>{err}</ErrorText>

      {list && list.length === 0 && (
        <Empty>
          No setup has been imported yet. Export the treatment plan from ArchForm (or uLab, 3Shape or
          Blue Sky Plan) and upload it above - the doctor reviews and approves it in their portal.
        </Empty>
      )}

      {list && list.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {list.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setOpenId(s.id)}
              className={`tap rounded-md px-3 py-1.5 text-sm font-medium transition ${
                openId === s.id ? 'bg-primary text-primary-fg' : 'bg-surface-2 text-ink hover:bg-line'
              }`}
            >
              r{s.revision}
              <span className="ml-1.5 text-[11px] opacity-75">{SETUP_STATUS_LABELS[s.status]}</span>
            </button>
          ))}
        </div>
      )}

      {view && (
        <>
          <RevisionHeader
            view={view}
            trays={ws.case.trays}
            priority={ws.case.priority}
            canEdit={canEdit}
            busy={busy}
            onShare={() => run(() => wfApi.shareSetup(view.setup.id))}
            onRevoke={() => run(() => wfApi.revokeSetupShare(view.setup.id))}
            onTraysSet={onChanged}
          />
          <SetupWorkspace
            view={view}
            busy={busy}
            onAnnotate={(input) => run(() => wfApi.annotateSetup(view.setup.id, input))}
          />
          {canEdit && view.annotations.some((a) => a.authorSide === 'clinic' && !a.resolvedAt) && (
            <OpenRequests view={view} busy={busy} onResolve={(id) => run(() => wfApi.resolveSetupAnnotation(view.setup.id, id))} />
          )}
        </>
      )}
    </div>
  );
}

function RevisionHeader({
  view,
  trays,
  priority,
  canEdit,
  busy,
  onShare,
  onRevoke,
  onTraysSet,
}: {
  view: SetupView;
  trays: number | null;
  priority: WfPriority | null;
  canEdit: boolean;
  busy: boolean;
  onShare: () => void;
  onRevoke: () => void;
  onTraysSet: () => void;
}) {
  const s = view.setup;
  const [copied, setCopied] = useState(false);
  const shareUrl = s.shareToken ? `${window.location.origin}/setup/${s.shareToken}` : null;

  // Sharing is gated on the aligner count: the count sets the price and raises the invoice, so a
  // setup can't go out for approval before the commercial side of it exists.
  if (canEdit && !(trays && trays > 0)) {
    return (
      <div className="space-y-3">
        <TrayCountRequired caseId={s.caseId} onSet={onTraysSet} priority={priority} />
        <p className="text-xs text-muted">Set the number of aligners to share this setup with the doctor.</p>
      </div>
    );
  }

  return (
    <section className="rounded-lg border border-line bg-surface p-4 shadow-level-1">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-ink">
            Setup r{s.revision} · {SETUP_STATUS_LABELS[s.status]}
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            {SETUP_SOURCE_LABELS[s.source]}
            {s.sourceVersion ? ` ${s.sourceVersion}` : ''} · {s.stepCount} steps · {s.arches.join(' + ') || 'no arch'}
            {s.createdByName ? ` · imported by ${s.createdByName}` : ''} · {fmtDT(s.createdAt)}
          </p>
          {s.notes && <p className="mt-1 text-sm text-ink">{s.notes}</p>}
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-1.5">
            {s.status !== 'approved' && (
              <Btn tone="accent" disabled={busy} onClick={onShare}>
                {s.status === 'shared' ? 'Re-share' : 'Send to the doctor'}
              </Btn>
            )}
            {s.shareToken && (
              <Btn tone="ghost" disabled={busy} onClick={onRevoke}>
                Revoke link
              </Btn>
            )}
          </div>
        )}
      </div>

      {shareUrl && (
        <div className="mt-3 rounded-md bg-surface-2 p-2.5">
          <p className="mb-1 text-xs text-muted">
            Anyone with this link can view the setup without signing in
            {s.shareExpiresAt ? ` - it stops working on ${fmtDT(s.shareExpiresAt)}` : ''}.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input readOnly value={shareUrl} className={`${inputCls} min-w-0 flex-1 font-mono text-xs`} onFocus={(e) => e.currentTarget.select()} />
            <Btn
              tone="subtle"
              onClick={() => {
                void navigator.clipboard?.writeText(shareUrl).then(() => {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1800);
                });
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </Btn>
          </div>
        </div>
      )}
    </section>
  );
}

/** What the doctor has asked for and the lab hasn't applied yet. */
function OpenRequests({ view, busy, onResolve }: { view: SetupView; busy: boolean; onResolve: (id: string) => void }) {
  const open = view.annotations.filter((a) => a.authorSide === 'clinic' && !a.resolvedAt);
  return (
    <section className="rounded-lg border border-status-warn-fg/30 bg-status-warn-bg/50 p-4">
      <h2 className="text-sm font-semibold text-status-warn-fg">Waiting on the technician ({open.length})</h2>
      <p className="mb-2 mt-0.5 text-xs text-status-warn-fg/90">
        Apply these in the design software, then import the corrected plan as a new revision.
      </p>
      <ul className="space-y-1.5">
        {open.map((a) => (
          <li key={a.id} className="flex flex-wrap items-center gap-2 rounded-md bg-surface px-3 py-2 text-sm">
            <span className="min-w-0 flex-1">
              {a.toothId && <b className="tabnum">Tooth {a.toothId} </b>}
              {a.note ?? '-'}
            </span>
            <Btn tone="subtle" disabled={busy} onClick={() => onResolve(a.id)}>
              Mark applied
            </Btn>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ImportForm({ ws, onDone }: { ws: WfCaseWorkspace; onDone: (setupId: string) => void }) {
  const [plan, setPlan] = useState<File | null>(null);
  const [mesh, setMesh] = useState<File | null>(null);
  const [meshFileId, setMeshFileId] = useState('');
  const [notes, setNotes] = useState('');
  const [pct, setPct] = useState<number | null>(null);
  const [stage, setStage] = useState<'mesh' | 'plan'>('plan');
  const [warnings, setWarnings] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);

  // Either pick a GLB already attached to the case, or upload one here. Both end up going through
  // the ordinary file pipeline, so there is still one upload path with one set of checks.
  const meshes = ws.files.filter((f) => /\.(glb|gltf)$/i.test(f.originalName));

  const submit = async () => {
    if (!plan) return setErr('Choose the plan export first.');
    setErr(null);
    setWarnings([]);
    setPct(0);
    try {
      let meshId = meshFileId || null;
      if (mesh) {
        setStage('mesh');
        const up = await uploadCaseFile(ws.case.id, mesh, { category: 'setup', description: 'Setup mesh' }, setPct);
        // A .glb is never an archive, but the upload endpoint can return either shape.
        if (isArchiveOutcome(up)) throw new Error('Upload the mesh itself, not an archive of it');
        meshId = up.id;
      }
      setStage('plan');
      setPct(0);
      const r = await importSetupPlan(ws.case.id, plan, { meshFileId: meshId, notes }, setPct);
      setWarnings(r.warnings);
      setPlan(null);
      setMesh(null);
      setNotes('');
      onDone(r.setup.id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setPct(null);
    }
  };

  return (
    <section className="rounded-lg border border-line bg-surface p-4 shadow-level-1">
      <h2 className="text-sm font-semibold text-ink">Import a treatment plan</h2>
      <p className="mb-3 mt-0.5 text-xs text-muted">
        The plan export (JSON) carries the per-step tooth positions. Add the segmented GLB alongside
        it - one node per tooth, named by FDI number - and the doctor sees the real teeth rather than
        a schematic.
      </p>
      <div className="grid gap-2.5 sm:grid-cols-2">
        <label className="text-sm">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted">Plan export</span>
          <input
            type="file"
            accept=".json,application/json"
            onChange={(e) => setPlan(e.target.files?.[0] ?? null)}
            className="block w-full text-sm text-muted file:mr-3 file:rounded file:border-0 file:bg-surface-2 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-ink"
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted">Mesh (GLB)</span>
          <input
            type="file"
            accept=".glb,.gltf,model/gltf-binary"
            onChange={(e) => {
              setMesh(e.target.files?.[0] ?? null);
              if (e.target.files?.[0]) setMeshFileId('');
            }}
            className="block w-full text-sm text-muted file:mr-3 file:rounded file:border-0 file:bg-surface-2 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-ink"
          />
          {!mesh && meshes.length > 0 && (
            <select className={`${inputCls} mt-1.5`} value={meshFileId} onChange={(e) => setMeshFileId(e.target.value)}>
              <option value="">…or reuse one already on this case</option>
              {meshes.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.originalName}
                </option>
              ))}
            </select>
          )}
        </label>
        <label className="text-sm sm:col-span-2">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted">Note for the doctor</span>
          <input className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
        </label>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Btn tone="primary" disabled={pct !== null || !plan} onClick={() => void submit()}>
          {pct !== null ? `${stage === 'mesh' ? 'Uploading the mesh' : 'Importing'}… ${pct}%` : 'Import as a new revision'}
        </Btn>
        {plan && <span className="text-xs text-muted">{plan.name}</span>}
      </div>

      <ErrorText>{err}</ErrorText>
      {warnings.length > 0 && (
        <ul className="mt-2 list-disc space-y-0.5 pl-5 text-xs text-status-warn-fg">
          {warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}
    </section>
  );
}
